'use client';

import React from 'react';

import Icon from '@/components/ui/AppIcon';

// Bento grid plan: 8 cards → grid-cols-4
// Row 1: Hero Revenue (spans 2 cols, tall) + Gross Profit + Outstanding Receivables
// Row 2: Orders in Pipeline + Delivery Rate + Inventory Value + Collections Due
// Total: 1 hero(2-col) + 3 regular = row1; 4 regular = row2

const agingBuckets = [
  { label: 'Current', amount: 'XCD 284,500', pct: 47, color: 'bg-positive' },
  { label: '1–30 days', amount: 'XCD 142,300', pct: 23, color: 'bg-warning' },
  { label: '31–60 days', amount: 'XCD 98,800', pct: 16, color: 'bg-accent' },
  { label: '61–90 days', amount: 'XCD 54,200', pct: 9, color: 'bg-negative' },
  { label: '90+ days', amount: 'XCD 29,100', pct: 5, color: 'bg-negative' },
];

const orderPipelineCounts = [
  { label: 'Draft', count: 4, color: 'bg-muted-foreground' },
  { label: 'Submitted', count: 11, color: 'bg-info' },
  { label: 'Approved', count: 8, color: 'bg-positive' },
  { label: 'Picking', count: 6, color: 'bg-warning' },
  { label: 'Packed', count: 5, color: 'bg-accent' },
  { label: 'Out for Delivery', count: 9, color: 'bg-info' },
];

export default function ExecutiveBentoGrid() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {/* HERO: Revenue MTD — spans 2 cols */}
      <div className="col-span-1 md:col-span-2 lg:col-span-2 xl:col-span-2 2xl:col-span-2 rounded-xl gradient-green border border-primary/30 shadow-card p-5 flex flex-col gap-4 card-hover">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-xs font-600 uppercase tracking-widest text-white/60">Revenue MTD</p>
            <p className="font-mono-nums text-4xl font-700 text-white mt-1">XCD 2,847,340</p>
            <p className="text-sm text-white/60 mt-0.5">≈ USD 420,200 · Jul 1–31, 2026</p>
          </div>
          <div className="flex items-center gap-1.5 bg-white/20 rounded-lg px-2.5 py-1.5">
            <Icon name="ArrowTrendingUpIcon" size={14} className="text-white" />
            <span className="text-xs font-700 text-white">+12.4%</span>
          </div>
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs">
            <span className="text-white/60 font-500">vs. Target XCD 3,200,000</span>
            <span className="text-white font-700">89.0%</span>
          </div>
          <div className="w-full bg-white/20 rounded-full h-2">
            <div className="bg-white rounded-full h-2 transition-all duration-500" style={{ width: '89%' }} />
          </div>
          <div className="flex items-center justify-between text-xs text-white/50">
            <span>XCD 352,660 remaining to target</span>
            <span>Last month: XCD 2,531,800</span>
          </div>
        </div>
        <div className="grid grid-cols-3 gap-3 pt-2 border-t border-white/20">
          {[
            { label: 'Invoiced', value: 'XCD 2,614,100' },
            { label: 'Collected', value: 'XCD 1,982,500' },
            { label: 'Pending', value: 'XCD 631,600' },
          ]?.map((item) => (
            <div key={`rev-${item?.label}`} className="text-center">
              <p className="font-mono-nums text-sm font-700 text-white">{item?.value}</p>
              <p className="text-2xs text-white/50 mt-0.5">{item?.label}</p>
            </div>
          ))}
        </div>
      </div>
      {/* Gross Profit Margin */}
      <div className="rounded-xl border border-border bg-card shadow-card p-4 flex flex-col gap-3 card-hover">
        <div className="flex items-start justify-between">
          <div className="w-9 h-9 rounded-lg bg-positive/10 flex items-center justify-center">
            <Icon name="ChartPieIcon" size={18} className="text-positive" />
          </div>
          <span className="text-xs font-600 text-positive flex items-center gap-1">
            <Icon name="ArrowTrendingUpIcon" size={13} className="text-positive" />
            +1.2pp
          </span>
        </div>
        <div>
          <p className="text-xs font-500 uppercase tracking-wide text-muted-foreground">Gross Profit</p>
          <p className="font-mono-nums text-2xl font-700 text-foreground mt-0.5">34.8%</p>
          <p className="text-xs text-muted-foreground mt-0.5">XCD 990,877 GP value</p>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-xs text-muted-foreground">
            <span>Target: 35%</span><span>Last mo: 33.6%</span>
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div className="bg-positive rounded-full h-1.5" style={{ width: '99.4%' }} />
          </div>
        </div>
      </div>
      {/* Outstanding Receivables */}
      <div className="rounded-xl border border-warning/30 bg-warning/5 shadow-card p-4 flex flex-col gap-3 card-hover">
        <div className="flex items-start justify-between">
          <div className="w-9 h-9 rounded-lg bg-warning/10 flex items-center justify-center">
            <Icon name="BanknotesIcon" size={18} className="text-warning" />
          </div>
          <div className="flex items-center gap-1 text-xs font-600 text-negative">
            <Icon name="ArrowTrendingUpIcon" size={13} className="text-negative" />
            +8.3%
          </div>
        </div>
        <div>
          <p className="text-xs font-500 uppercase tracking-wide text-muted-foreground">Receivables</p>
          <p className="font-mono-nums text-2xl font-700 text-foreground mt-0.5">XCD 608,900</p>
          <p className="text-xs text-muted-foreground mt-0.5">Across 47 accounts</p>
        </div>
        <div className="space-y-1">
          {agingBuckets?.slice(0, 3)?.map((b) => (
            <div key={`aging-${b?.label}`} className="flex items-center gap-2">
              <div className="flex-1 bg-muted rounded-full h-1.5">
                <div className={`${b?.color} rounded-full h-1.5`} style={{ width: `${b?.pct}%` }} />
              </div>
              <span className="text-2xs text-muted-foreground w-12 text-right">{b?.label}</span>
            </div>
          ))}
        </div>
      </div>
      {/* Orders in Pipeline */}
      <div className="rounded-xl border border-border bg-card shadow-card p-4 flex flex-col gap-3 card-hover">
        <div className="flex items-start justify-between">
          <div className="w-9 h-9 rounded-lg bg-info/10 flex items-center justify-center">
            <Icon name="ClipboardDocumentListIcon" size={18} className="text-info" />
          </div>
          <span className="text-xs font-600 text-muted-foreground">43 active</span>
        </div>
        <div>
          <p className="text-xs font-500 uppercase tracking-wide text-muted-foreground">Orders Pipeline</p>
          <p className="font-mono-nums text-2xl font-700 text-foreground mt-0.5">43</p>
          <p className="text-xs text-muted-foreground mt-0.5">XCD 384,200 in value</p>
        </div>
        <div className="flex flex-wrap gap-1">
          {orderPipelineCounts?.map((s) => (
            <span key={`pipe-${s?.label}`} className="text-2xs font-600 px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground">
              {s?.count} {s?.label}
            </span>
          ))}
        </div>
      </div>
      {/* Delivery Completion Rate */}
      <div className="rounded-xl border border-border bg-card shadow-card p-4 flex flex-col gap-3 card-hover">
        <div className="flex items-start justify-between">
          <div className="w-9 h-9 rounded-lg bg-positive/10 flex items-center justify-center">
            <Icon name="TruckIcon" size={18} className="text-positive" />
          </div>
          <span className="text-xs font-600 text-positive flex items-center gap-1">
            <Icon name="ArrowTrendingUpIcon" size={13} className="text-positive" /> +2.1%
          </span>
        </div>
        <div>
          <p className="text-xs font-500 uppercase tracking-wide text-muted-foreground">Delivery Rate</p>
          <p className="font-mono-nums text-2xl font-700 text-foreground mt-0.5">94.7%</p>
          <p className="text-xs text-muted-foreground mt-0.5">18 of 19 today delivered</p>
        </div>
        <div className="space-y-1">
          <div className="flex justify-between text-xs">
            <span className="text-muted-foreground">Target: 95%</span>
            <span className="text-warning font-600">1 failed today</span>
          </div>
          <div className="w-full bg-muted rounded-full h-1.5">
            <div className="bg-positive rounded-full h-1.5" style={{ width: '94.7%' }} />
          </div>
        </div>
      </div>
      {/* Inventory Value */}
      <div className="rounded-xl border border-border bg-card shadow-card p-4 flex flex-col gap-3 card-hover">
        <div className="flex items-start justify-between">
          <div className="w-9 h-9 rounded-lg bg-secondary flex items-center justify-center">
            <Icon name="CubeIcon" size={18} className="text-primary" />
          </div>
          <span className="text-xs font-600 text-muted-foreground">1,284 SKUs</span>
        </div>
        <div>
          <p className="text-xs font-500 uppercase tracking-wide text-muted-foreground">Inventory Value</p>
          <p className="font-mono-nums text-2xl font-700 text-foreground mt-0.5">XCD 1,142,800</p>
          <p className="text-xs text-muted-foreground mt-0.5">≈ USD 168,800</p>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-warning font-600 flex items-center gap-1">
            <Icon name="ExclamationTriangleIcon" size={12} className="text-warning" />
            23 low stock
          </span>
          <span className="text-negative font-600">7 out of stock</span>
        </div>
      </div>
      {/* Collections Due Today */}
      <div className="rounded-xl border border-negative/30 bg-negative/5 shadow-card p-4 flex flex-col gap-3 card-hover">
        <div className="flex items-start justify-between">
          <div className="w-9 h-9 rounded-lg bg-negative/10 flex items-center justify-center">
            <Icon name="ReceiptPercentIcon" size={18} className="text-negative" />
          </div>
          <span className="text-xs font-700 text-negative">URGENT</span>
        </div>
        <div>
          <p className="text-xs font-500 uppercase tracking-wide text-muted-foreground">Collections Due</p>
          <p className="font-mono-nums text-2xl font-700 text-foreground mt-0.5">XCD 87,400</p>
          <p className="text-xs text-muted-foreground mt-0.5">Due today — 12 accounts</p>
        </div>
        <div className="flex items-center justify-between text-xs">
          <span className="text-muted-foreground">Collected: XCD 34,100</span>
          <span className="text-negative font-600">XCD 53,300 remaining</span>
        </div>
      </div>
    </div>
  );
}